import polyline from '@mapbox/polyline'

export const MAPS_KEY = 'AIzaSyA63E33ZAzfRnM3_pWAlRU32QFXNFk9kts'
export const MAPS_URL = 'https://geocache-dev.gravitate.energy'
export const MAPS_FALLBACK_URL = 'https://maps.googleapis.com'

export type LatLng = {
  lat: number
  lng: number
}

export type GoogleLatLng = {
  lat: () => number
  lng: () => number
}

export function buildDirectionsRequest(
  origin: LatLng,
  destination: LatLng,
  waypoints = [] as { location: GoogleLatLng }[]
) {
  const params = new URLSearchParams({
    origin: `${origin.lat},${origin.lng}`,
    destination: `${destination.lat},${destination.lng}`,
    travelMode: 'driving',
  })

  if (waypoints.length) {
    const waypointsParams = waypoints?.map((wp) => `${wp.location.lat()},${wp.location.lng()}`).join('|')
    params.append('waypoints', waypointsParams)
  }

  const request = {
    origin: new google.maps.LatLng(origin.lat, origin.lng),
    destination: new google.maps.LatLng(destination.lat, destination.lng),
    travelMode: google.maps.TravelMode.DRIVING,
    waypoints: waypoints,
  }

  return { request, params }
}

export type DirectionsRequest = {
  origin: LatLng
  destination: LatLng
  travelMode: string
  waypoints: { location: GoogleLatLng }[]
}

// do a health check on our caching service and fall back to google maps if it fails
async function getRequestBaseUrl() {
  try {
    const resp = await fetch(`${MAPS_URL}/health`)

    if (!resp.ok) {
      return MAPS_FALLBACK_URL
    }

    const text = await resp.text()

    if (!text.includes('ok')) {
      return MAPS_FALLBACK_URL
    }

    return MAPS_URL
  } catch (error) {
    console.error('Error fetching health check:', error)
    return MAPS_FALLBACK_URL
  }
}

export async function fetchDirections(request: DirectionsRequest, params: URLSearchParams) {
  try {
    const baseUrl = await getRequestBaseUrl()
    const requestUrl = `${baseUrl}/maps/api/directions/json?${params}&key=${MAPS_KEY}`

    const resp = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Maps-API-Key': MAPS_KEY,
      },
      body: JSON.stringify(request),
    })

    const json = await resp.json()
    return json
  } catch (error) {
    console.error('Error fetching directions:', error)
    throw error
  }
}

/** Modifies a google maps directions HTTPs response in place and converts the neccesarry lat / lng literals needed for the DirectionsRenderer to display the response correctly. */
export function fixMapsApiResponse(request: DirectionsRequest, response: any) {
  const _origin = request.origin
  const _destination = request.destination

  response.request = {
    ...request,
    origin: { location: _origin },
    destination: { location: _destination },
  }

  response.routes = response.routes.map((route) => {
    const bounds = new google.maps.LatLngBounds()
    bounds.extend(route.bounds.northeast)
    bounds.extend(route.bounds.southwest)
    route.bounds = bounds
    route.overview_path = polyline.decode(route.overview_polyline.points).map((point) => {
      return new google.maps.LatLng(point[0], point[1])
    })

    route.legs = route.legs.map((leg) => {
      leg.steps = leg.steps.map((step) => {
        step.path = polyline.decode(step.polyline.points).map((point) => {
          return new google.maps.LatLng(point[0], point[1])
        })
        return step
      })
      return leg
    })

    return route
  })
}
